import { Anchor } from "@components/Anchor";
import { Body } from "@components/Typography";
import { TextHighlighterProps } from "./types";

export const TextHighlighter = ({
  text,
  words,
  TypographyComponent = Body,
  style,
  highlightColor,
}: TextHighlighterProps) => {
  let result = text;
  const replacements: Array<{
    start: number;
    end: number;
    word: string;
    url?: string;
    onClickIndex?: number;
  }> = [];
  const onClickHandlers: (() => void)[] = [];

  words.forEach(({ text: wordText, url, onClick }) => {
    const regex = new RegExp(`\\b${wordText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "gi");
    let match;
    while ((match = regex.exec(result)) !== null) {
      let onClickIndex: number | undefined;
      if (onClick) {
        onClickIndex = onClickHandlers.length;
        onClickHandlers.push(onClick);
      }

      replacements.push({
        start: match.index,
        end: match.index + match[0].length,
        word: match[0],
        url,
        onClickIndex,
      });
    }
  });

  replacements.sort((a, b) => b.start - a.start);

  replacements.forEach(({ start, end, word, url, onClickIndex }) => {
    const before = result.substring(0, start);
    const after = result.substring(end);
    const hasUrl = url !== undefined;
    const hasOnClick = onClickIndex !== undefined;

    if (hasUrl) {
      result = before + `__ANCHOR_START__${word}__ANCHOR_URL__${url}__ANCHOR_END__` + after;
    } else if (hasOnClick) {
      result =
        before + `__ONCLICK_START__${word}__ONCLICK_INDEX__${onClickIndex}__ONCLICK_END__` + after;
    } else if (highlightColor) {
      result = before + `__HIGHLIGHT_START__${word}__HIGHLIGHT_END__` + after;
    }
  });

  const parts = result.split(
    /(__ANCHOR_START__.*?__ANCHOR_END__|__ONCLICK_START__.*?__ONCLICK_END__|__HIGHLIGHT_START__.*?__HIGHLIGHT_END__)/,
  );

  return (
    <TypographyComponent style={style}>
      {parts.map((part, index) => {
        if (part.startsWith("__ANCHOR_START__")) {
          const match = part.match(/__ANCHOR_START__(.*?)__ANCHOR_URL__(.*?)__ANCHOR_END__/);
          if (match) {
            return (
              <Anchor
                key={index}
                href={match[2]}
                style={highlightColor ? { color: highlightColor } : undefined}
              >
                {match[1]}
              </Anchor>
            );
          }
        } else if (part.startsWith("__ONCLICK_START__")) {
          const match = part.match(/__ONCLICK_START__(.*?)__ONCLICK_INDEX__(.*?)__ONCLICK_END__/);
          if (match) {
            const onClickIndex = parseInt(match[2], 10);
            const onClickHandler = onClickHandlers[onClickIndex];
            return (
              <Anchor
                key={index}
                onClick={onClickHandler}
                style={{
                  cursor: "pointer",
                  ...(highlightColor && { color: highlightColor }),
                }}
              >
                {match[1]}
              </Anchor>
            );
          }
        } else if (part.startsWith("__HIGHLIGHT_START__")) {
          const match = part.match(/__HIGHLIGHT_START__(.*?)__HIGHLIGHT_END__/);
          if (match && highlightColor) {
            return (
              <span key={index} style={{ color: highlightColor }}>
                {match[1]}
              </span>
            );
          }
        }
        return part;
      })}
    </TypographyComponent>
  );
};
